import React, { useState } from 'react';
import Helmet from 'react-helmet/es/Helmet';
import { Switch, Route, Link } from 'react-router-dom';
import { API } from 'aws-amplify';
import IconButton from '@material-ui/core/IconButton';
import Alert from '@material-ui/lab/Alert';
import Typography from '@material-ui/core/Typography';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { Box, Card, CardContent } from '@material-ui/core';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import { Query, useMutation } from 'react-apollo';
import MaterialTable from 'material-table';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Spin from 'components/common/Spin';
import {
  GET_USER_PAYMENT_ITEMS,
  GET_USER_INVOICES,
  GET_COACH,
  UPDATE_PAYMENT_ITEM,
} from 'graphql/payments';
import InvoiceDetails from 'components/pages/time-tracker/components/InvoiceDetails';
import BonusLogs from 'components/pages/time-tracker/components/BonusLogs';
import { getFullName } from 'utils';
import useSnack from 'hooks/useSnack';
import { INSERT_NOTIFICATION } from 'graphql/notification';
import { useSelector } from 'react-redux';
import Button from 'components/common/Button';
import Dropdown from 'components/form/Dropdown';
import { makeStyles } from '@material-ui/core/styles';

const useStyle = makeStyles((theme) => ({
  cardTitle: {
    margin: '1em'
  },
  dropdown: {
    width: 150,
  },
  resetButton: {
    borderRadius: 10,
    maxWidth: "200px",
    minWidth: "200px",
    marginTop: '2em',
  },
  table: {
    marginTop: '1em',
  }



}));


function pluck_status(array, key) {
  return array?.map((o) => o[key]).sort();
}

const TimeLogs = ({
  match: {
    params: { userId },
  },
}) => {
  const snack = useSnack();
  const classes = useStyle();
  const [updateItem] = useMutation(UPDATE_PAYMENT_ITEM);
  const [insertNotification] = useMutation(INSERT_NOTIFICATION);
  const [filterStatus, setFilterStatus] = useState('');
  const user = useSelector((s) => s.user.user);

  const resetAllFilter = () => {
    setFilterStatus();
  }

  const columns = [
    { title: 'Date', field: 'date', type: 'date' },
    {
      title: 'Status', field: 'status',
      defaultFilter: filterStatus,
      customFilterAndSearch: (term, rowData) => {
        if (rowData?.status === term)
          return rowData;
      }
    },
    { title: 'Item', field: 'payment_category.label' },
    {
      title: 'Student/Group',
      field: 'student.user_first_name',
      render: (row) => {
        if (row.group) return row.group.name;
        return getFullName(row.student);
      },
    },
    {
      title: 'Time',
      field: 'duration',
      render: (row) => `${row.duration} min`,
    },
    {
      title: 'Amount',
      field: 'total',
      render: (row) => `$${row.total / 100}`,
      type: 'numeric',
    },
    { title: 'Comment', field: 'comments' },
  ];
  return (
    <Query
      query={GET_USER_PAYMENT_ITEMS}
      variables={{ userId, status: ['submitted', 'pending', 'approved'] }}
      fetchPolicy={'cache-and-network'}
    >
      {({ data, loading, error, refetch }) => {
        if (loading) return <Spin spinning />;
        if (error) return error.message;

        const { rows } = data;
        let status = pluck_status(rows, 'status'); //getting all the status
        let unique_status = [...new Set(status)];  //getting unique status
        let filtered_status = unique_status?.filter(  //filtering out the status that are not in the status array
          (val) => val !== null && val !== undefined,
        );
        const status_option = filtered_status?.map(i => ({ value: i, label: i })) //creating the status options for dropwdown

        const onGenerateInvoice = async () => {
          if (
            window.confirm(
              'Confirm generate invoice and payout? This will create a payout with all the approved timelogs.',
            )
          ) {
            try {
              await API.post('api', `/admin/payouts/${userId}/create`);
              snack.success('Invoice successfully created');
              refetch();
            } catch (e) {
              snack.error(e.message);
            }
          }
        };

        const onApprove = async (row) => {
          if (window.confirm('Confirm approve?')) {
            await Promise.all(
              row.map((r) => {
                updateItem({
                  variables: {
                    id: r.id,
                    data: {
                      status: 'approved',
                      approved: true,
                      approved_by: user.id,
                    },
                  },
                });
              }),
            );
          }
        };

        const onReject = async (row) => {
          if (window.confirm('Confirm reject?')) {
            await Promise.all(
              row.map((r) => {
                updateItem({
                  variables: {
                    id: r.id,
                    data: {
                      status: 'rejected',
                      approved: false,
                    },
                  },
                });
                insertNotification({
                  variables: {
                    data: {
                      actor_id: r.user_id,
                      message: `Your time log entry ${r.payment_category.label} was rejected`,
                      recipient_id: r.user_id,
                      subject: 'payment_line_items',
                      subject_id: r.id,
                      topic: 'TIMELOG_REJECTED',
                    },
                  },
                });
              }),
            );
          }
        };

        return (
          <Box>
            <Box>
              <Typography paragraph variant="h5">
                Filters
              </Typography>
              <Card>
                <Typography
                  className={classes.cardTitle}
                  paragraph
                  variant="subtitle2"
                >
                  Filter by
                </Typography>

                <CardContent className={""}>
                  <Box paddingRight={10}>
                    <Typography
                      gutterBottom
                      variant="smallBold"
                      color="textSecondary"
                      component="p"
                    >
                      Status
                    </Typography>
                    <Dropdown
                      className={classes.dropdown}
                      options={status_option}
                      value={filterStatus || null}
                      onChange={val => setFilterStatus(val)}
                    />
                  </Box>
                  <Box margin={1} style={{ textAlign: 'center' }}>
                    <Button
                      className={classes.resetButton}
                      fullWidth
                      variant="outlined"
                      size="large"
                      color="primary"
                      onClick={resetAllFilter}
                    >
                      Reset
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box className={classes.table}>
              <MaterialTable
                title="Time Log"
                options={{
                  search: true,
                  actionsColumnIndex: -1,
                  pageSize: 10,
                  selection: true,
                }}
                columns={columns}
                data={rows}
                actions={[
                  {
                    disabled: !rows.length,
                    icon: PlaylistAddCheckIcon,
                    tooltip: 'Generate Invoice',
                    isFreeAction: true,
                    onClick: onGenerateInvoice,
                  },
                  (rowData) =>
                  ({
                    icon: 'check',
                    tooltip: 'Approve All Selected',
                    onClick: (event, rowData) => onApprove(rowData),
                  }),
                  (rowData) =>
                  ({
                    icon: 'block',
                    tooltip: 'Reject All Selected',
                    onClick: (event, rowData) => onReject(rowData),
                  }),
                ]}
              />
            </Box>
          </Box>
        );
      }}
    </Query>
  );
};

const Invoices = ({
  match: {
    params: { userId },
  },
}) => {
  const [selected, setSelected] = useState();
  const snack = useSnack();

  return (
    <Query
      query={GET_USER_INVOICES}
      variables={{ userId }}
      fetchPolicy={'cache-and-network'}
    >
      {({ data, loading, refetch }) => {
        if (loading) return <Spin spinning />;
        const { rows } = data;

        const onRetryPayment = async (e, invoice) => {
          e.stopPropagation();
          try {
            await API.post('api', `/admin/payouts/invoices/${invoice.id}/pay`);
            snack.success('Invoice successfully paid');
            refetch();
          } catch (e) {
            snack.error(e.message);
          }
        }

        const columns = [
          { title: 'Created At', field: 'created_at', type: 'date' },
          {
            title: 'Amount',
            field: 'total',
            render: (row) => `$${row.total / 100}`,
            type: 'numeric',
          },
          {
            title: 'status', field: 'status', render: (row) => {
              if (row.status === 'paid') return 'Paid';
              else if (row.last_attempt_error) {
                return (
                  <>
                    <span>{row.status}</span>
                    <Button onClick={(e) => onRetryPayment(e, row)} color="primary" size="small" variant="text">Retry</Button>
                  </>
                )
              }
              return row.status;
            }
          },
          { title: 'Paid At', field: 'paid_at', type: 'date' },
          {
            title: 'Status Message',
            field: 'last_attempt_error',
          },
        ];

        return (
          <Box>
            <MaterialTable
              title="Invoices"
              options={{
                search: false,
                actionsColumnIndex: -1,
                pageSize: 10,
              }}
              columns={columns}
              data={rows}
              onRowClick={(e, row) => setSelected(row)}
            />
            {selected && (
              <InvoiceDetails id={selected.id} onClose={() => setSelected()} />
            )}
          </Box>
        );
      }}
    </Query>
  );
};

const AccountDetails = ({
  location,
  match: {
    params: { userId },
  },
}) => {
  const tabs = [
    `/payments/accounts/${userId}`,
    `/payments/accounts/${userId}/invoices`,
    `/payments/accounts/${userId}/bonus`,
  ];

  return (
    <Box>
      <Helmet title="Payments: Account details" />
      <Query query={GET_COACH} variables={{ id: userId }}>
        {({ data, loading }) => {
          if (!data) return null;
          return (
            <Box marginBottom={2} style={{ marginTop: -20 }}>
              <Box display="flex" alignItems="center">
                <Link to={'/payments'}>
                  <IconButton variant="text" color={'primary'}>
                    <ArrowBackIcon />
                  </IconButton>
                </Link>
                <Typography variant="h6">{getFullName(data.user)}</Typography>
              </Box>
              {data.user.payment_account &&
                !data.user.payment_account.payouts_enabled && (
                  <Alert color="warning">
                    User has not completed payment account setup. No payout will
                    be sent until user has completed account verification
                    process.
                  </Alert>
                )}
            </Box>
          );
        }}
      </Query>
      <Box marginBottom={1}>
        <Tabs
          value={location.pathname}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            value={tabs[0]}
            label="Time logs"
            component={Link}
            to={tabs[0]}
          />
          <Tab value={tabs[1]} label="Invoices" component={Link} to={tabs[1]} />
          <Tab value={tabs[2]} label="Bonus History" component={Link} to={tabs[2]} />
        </Tabs>
      </Box>
      <Switch>
        <Route exact path="/payments/accounts/:userId" component={TimeLogs} />
        <Route
          exact
          path="/payments/accounts/:userId/invoices"
          component={Invoices}
        />
        <Route
          exact
          path="/payments/accounts/:userId/bonus"
          render={(props) => <BonusLogs {...props} userId={props.match.params.userId} />}
        />
      </Switch>
    </Box>
  );
};

export default AccountDetails;
